import { useEffect, useState } from "react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { MapPin, ClockCounterClockwise, User, HardHat, ArrowRight, CaretLeft, CaretRight, Copy, Trash } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CHANTIER_TYPE_META, chantierTypes } from "@/pages/chantier/constants";

const STATUS_META = {
  waiting: { label: "En attente", color: "#9CA3AF" },
  in_progress: { label: "En cours", color: "#005CFF" },
  urgent: { label: "Urgent", color: "#EF4444" },
  done: { label: "Terminé", color: "#10B981" },
};

/**
 * Wraps `children` in a floating preview card that appears on hover with:
 *   - Chantier name + type badge · Client name
 *   - Address (Google Maps link) · Cover photo · Next intervention
 *   - CTA "Ouvrir la fiche →"
 *
 * When an `intervention` + `actions` object is passed, we also render a
 * quick action bar (trash / left / right / duplicate) with tooltips. Actions
 * are wired through `actions` so the parent decides *how* to shrink/delete
 * (typically: shrink on multi-day edges, DELETE on single-day).
 *
 * On touch devices, the HoverCard from Radix simply won't open — the tap
 * goes straight to the underlying button. That's the desired behaviour.
 */
export default function ChantierHoverPreview({
  chantier,
  client,
  currentIv,
  intervention,
  dayIso,
  actions,
  children,
  side = "right",
}) {
  const nav = useNavigate();
  const [photo, setPhoto] = useState(null);
  const [nextIv, setNextIv] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [opening, setOpening] = useState(false);

  const loadDetails = async () => {
    if (loaded || !chantier?.id) return;
    setLoaded(true);
    try {
      const now = new Date().toISOString();
      const [pRes, ivRes] = await Promise.all([
        api.get("/photos", { params: { chantier_id: chantier.id } }).catch(() => ({ data: [] })),
        api.get("/interventions", { params: { chantier_id: chantier.id } }).catch(() => ({ data: [] })),
      ]);
      const photos = pRes.data || [];
      const first = photos.find((p) => p.url || p.thumb_url || p.storage_path) || null;
      if (first) setPhoto(first.thumb_url || first.url || null);
      const upcoming = (ivRes.data || [])
        .filter((i) => i.scheduled_date && i.scheduled_date > now && i.id !== currentIv?.id && i.status !== "done")
        .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date))[0];
      if (upcoming) setNextIv(upcoming);
    } catch { /* silent */ }
  };

  if (!chantier && !intervention) return children;
  const status = chantier ? (STATUS_META[chantier.status] || STATUS_META.waiting) : null;
  const chTypes = chantierTypes(chantier).map((v) => ({ v, ...(CHANTIER_TYPE_META[v] || {}) })).filter((t) => t.label);
  const primaryType = chTypes[0] || null;
  const showActions = !!(intervention && actions);

  return (
    <HoverCard openDelay={220} closeDelay={140} onOpenChange={(o) => o && loadDetails()}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent
        side={side}
        align="start"
        sideOffset={8}
        className="w-72 p-0 rounded-2xl border border-white/20 bg-[#0A0A0A] text-white shadow-2xl shadow-black/60 overflow-hidden"
        data-testid={`chantier-hover-preview-${chantier?.id || intervention?.id || "iv"}`}
      >
        {/* Cover photo (or gradient placeholder) */}
        {chantier ? (
          <div
            className="relative h-24 w-full"
            style={{ background: photo ? undefined : `linear-gradient(135deg, ${primaryType?.color || "#005CFF"}55, ${status.color}22)` }}
          >
            {photo && (
              <img src={photo} alt="" className="w-full h-full object-cover" loading="lazy" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <div className="absolute top-2 right-2 flex gap-1">
              <span
                className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full font-semibold"
                style={{ background: `${status.color}CC`, color: "#fff" }}
              >
                {status.label}
              </span>
            </div>
            {chTypes.length > 0 && (
              <div className="absolute top-2 left-2 flex flex-wrap gap-1 max-w-[70%]">
                {chTypes.slice(0, 3).map((t) => (
                  <div
                    key={t.v}
                    className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full font-semibold shadow"
                    style={{ background: `${t.color}CC`, color: "#fff" }}
                  >
                    <span>{t.icon}</span> {t.label}
                  </div>
                ))}
                {chTypes.length > 3 && (
                  <div className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/20 text-white font-semibold">
                    +{chTypes.length - 3}
                  </div>
                )}
              </div>
            )}
            <div className="absolute bottom-2 left-3 right-3">
              <div className="text-[9px] uppercase tracking-widest text-white/70 flex items-center gap-1">
                <User size={9} weight="fill" /> {client?.name || "—"}
              </div>
              <div className="font-display font-semibold text-sm truncate drop-shadow" data-testid="hover-chantier-name">
                {chantier.name}
              </div>
            </div>
          </div>
        ) : (
          <div className="px-3 pt-3">
            <div className="font-display font-semibold text-sm truncate">{intervention.title || "Intervention"}</div>
            <div className="text-[11px] text-white/50 mt-0.5">
              {(intervention.scheduled_date || "").slice(0, 10)} · {(intervention.scheduled_date || "").slice(11, 16)}
            </div>
          </div>
        )}

        {/* Meta */}
        <div className="p-3 space-y-2">
          {chantier?.address && (
            <a
              href={`https://maps.google.com/?q=${encodeURIComponent(chantier.address)}`}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-start gap-1.5 text-[11px] text-white/70 hover:text-white transition-colors"
              data-testid="hover-address"
            >
              <MapPin size={12} weight="duotone" className="mt-0.5 shrink-0 text-[#7CB2FF]" />
              <span className="truncate">{chantier.address}</span>
            </a>
          )}
          {nextIv ? (
            <div className="flex items-start gap-1.5 text-[11px] text-emerald-300" data-testid="hover-next-iv">
              <ClockCounterClockwise size={12} weight="duotone" className="mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-[9px] uppercase tracking-widest text-emerald-400/80">Prochain rendez-vous</div>
                <div className="truncate">
                  {new Date(nextIv.scheduled_date).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })}
                  {" "}·{" "}
                  {new Date(nextIv.scheduled_date).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                </div>
                <div className="text-[10px] text-white/50 truncate">{nextIv.title}</div>
              </div>
            </div>
          ) : chantier && loaded ? (
            <div className="text-[10px] text-white/40 italic">Aucun rendez-vous à venir</div>
          ) : chantier && !loaded ? (
            <div className="text-[10px] text-white/30">Chargement…</div>
          ) : null}

          {/* Intervention quick actions */}
          {showActions && (
            <div className="pt-1 border-t border-white/10">
              <div className="text-[9px] uppercase tracking-widest text-white/40 mb-1.5">Actions rapides</div>
              <div className="grid grid-cols-4 gap-1.5" data-testid="hover-iv-actions">
                <IvAction
                  onClick={() => actions.onExtendLeft?.()}
                  label="J-1"
                  title="Ajouter le jour précédent"
                  icon={<CaretLeft size={13} weight="bold" />}
                  testid="hover-iv-left"
                />
                <IvAction
                  onClick={() => actions.onExtendRight?.()}
                  label="J+1"
                  title="Ajouter le jour suivant"
                  icon={<CaretRight size={13} weight="bold" />}
                  testid="hover-iv-right"
                />
                <IvAction
                  onClick={() => actions.onDuplicate?.()}
                  label="Copie"
                  title="Dupliquer sur un autre équipier / jour"
                  icon={<Copy size={13} weight="bold" />}
                  testid="hover-iv-duplicate"
                  variant="violet"
                />
                <IvAction
                  onClick={() => actions.onDelete?.()}
                  label="Suppr."
                  title={actions.deleteLabel || "Supprimer cette intervention"}
                  icon={<Trash size={13} weight="bold" />}
                  testid="hover-iv-delete"
                  variant="danger"
                />
              </div>
            </div>
          )}

          {chantier && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (opening) return;
                setOpening(true);
                nav(`/chantiers/${chantier.id}`);
              }}
              className="w-full mt-1 rounded-xl bg-[#005CFF] hover:bg-[#0047CC] px-3 py-2 text-xs font-semibold flex items-center justify-center gap-1.5 active:scale-95 transition-all"
              data-testid="hover-open-chantier"
            >
              <HardHat size={13} weight="duotone" />
              Ouvrir la fiche
              <ArrowRight size={12} weight="bold" />
            </button>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

function IvAction({ onClick, label, title, icon, testid, variant = "default" }) {
  const base = "flex flex-col items-center gap-0.5 py-1.5 rounded-lg border text-[10px] transition-all active:scale-95";
  const variants = {
    default: "bg-white/[0.04] border-white/10 hover:bg-white/[0.08] hover:border-white/25 text-white/80",
    violet: "bg-violet-500/10 border-violet-500/25 hover:bg-violet-500/20 hover:border-violet-500/50 text-violet-200",
    danger: "bg-red-500/10 border-red-500/25 hover:bg-red-500/20 hover:border-red-500/50 text-red-200",
  };
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClick?.(); }}
      className={`${base} ${variants[variant]}`}
      title={title}
      data-testid={testid}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
